/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import useSWR from 'swr';
import { Reorder, useDragControls } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import QueueMusicRoundedIcon from '@mui/icons-material/QueueMusicRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { Pathname } from 'Routes';
import { ReactComponent as DragIcon } from 'Assets/drag.svg';
import RotatingLoader from 'Components/RotatingLoader';
import { markSectionExplored, EXPLORED_SECTION } from 'Utils/onboardingChecklist';
import { isNewUIEnabled } from 'hooks/useNewUI';
import CreateSubCategory from './CreateSubCategory';
import styles from './index.module.css';

// The synthetic "Subcategories" row uses this sentinel id and is NOT reorderable.
const SUBCAT_ID = '#$%';

// Per-content-type icon + accent (light, clean command-center palette).
const typeMeta = (contentType) => {
  const t = (contentType || '').toLowerCase();
  if (t.includes('playlist')) return { Icon: QueueMusicRoundedIcon, tint: '#5aa5ef', bg: 'rgba(90,165,239,0.12)' };
  if (t.includes('categor') && t.includes('sub')) return { Icon: AccountTreeRoundedIcon, tint: '#12b3a6', bg: 'rgba(18,179,166,0.12)' };
  if (t.includes('categor')) return { Icon: GridViewRoundedIcon, tint: '#8b7cf6', bg: 'rgba(139,124,246,0.14)' };
  if (t.includes('photo') || t.includes('album')) return { Icon: PhotoLibraryRoundedIcon, tint: '#f5a524', bg: 'rgba(245,165,36,0.14)' };
  return { Icon: FolderRoundedIcon, tint: '#64748b', bg: 'rgba(100,116,139,0.12)' };
};

const countLabel = (count) => {
  if (count === null || count === undefined) return null;
  return `${count} ${count === 1 ? 'item' : 'items'}`;
};

// One draggable content-type row. dragListener is disabled so a drag only starts from
// the handle — the row body / Edit link stay clickable. framer-motion animates reflow.
const ContentTypeRow = ({ item, count, commonText, onSettle }) => {
  const controls = useDragControls();
  const { Icon, tint, bg } = typeMeta(item.content_type);
  const sub = countLabel(count) || 'Manage arrangement';

  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onSettle}
      className={styles.clRow}
      whileDrag={{ scale: 1.01, boxShadow: '0 14px 34px rgba(16,24,40,0.14)', zIndex: 5 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
    >
      <button
        type="button"
        className={styles.clDrag}
        aria-label="Drag to reorder"
        onPointerDown={(e) => controls.start(e)}
        style={{ touchAction: 'none' }}
      >
        <DragIndicatorRoundedIcon fontSize="small" />
      </button>

      <span className={styles.clIcon} style={{ backgroundColor: bg, color: tint }}>
        <Icon fontSize="small" />
      </span>

      <Link to={Pathname.getContentLayoutType(item.id)} className={styles.clMeta}>
        <span className={styles.clTitle}>{item.content_type}</span>
        <span className={styles.clSub}>{sub}</span>
      </Link>

      {count !== null && count !== undefined && (
        <span className={styles.clCount}>{count}</span>
      )}

      <Link to={Pathname.getContentLayoutType(item.id)} className={styles.clEdit}>
        {commonText?.edit || 'Edit'}
        <ChevronRightRoundedIcon fontSize="small" />
      </Link>
    </Reorder.Item>
  );
};

const ContentGroupButtonNew = ({ groups, counts = {} }) => {
  const { setAppSnackbar, langText } = useContext(AppContext);
  const commonText = langText?.common;
  const [items, setItems] = useState(groups || []);
  const [newOrder, updateNewOrder] = useState(undefined);
  const [loader, setloader] = useState(false);
  const [isOpenSubCategoryDiv, setSubCategoryDiv] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const { mutate: updateOrder } = useSWR([endpoints.reOrderMyContentType, newOrder?.items], {
    fetcher: (url, newItems) => dataFetcher(url, { items: newItems }),
    onSuccess: ({ success }) => {
      setloader(false);
      if (success) {
        // Ticks "Rearrange" off the dashboard getting-started checklist.
        markSectionExplored(EXPLORED_SECTION.rearrange);
      }
      setAppSnackbar({
        isVisible: true,
        ...(success ? { message: 'Order updated' } : { type: 'error', message: 'Oops! Something went wrong while updating your order' }),
      });
    },
    onError: (err) => {
      setloader(false);
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Reorder Content Types',
        description: 'Content type reorder failed',
        severity: 'medium',
        errorMessage: err?.response?.data?.message || err?.message || 'Content type reorder failed',
        responseStatus: err?.response?.status || null,
        responseBody: err?.response?.data?.message || null,
        requestUrl: endpoints.reOrderMyContentType,
        requestMethod: 'POST',
        component: 'ContentGroupButton',
        userAction: 'Reorder content types called',
      });
    },
  });

  useEffect(() => { setItems(groups || []); }, [groups]);
  useEffect(() => { if (newOrder) { setloader(true); updateOrder(); } }, [newOrder]);

  const realItems = items.filter((item) => item.id !== SUBCAT_ID);
  const subItem = items.find((item) => item.id === SUBCAT_ID);

  const handleReorder = (nextReal) => {
    setItems(subItem ? [...nextReal, subItem] : nextReal);
  };

  const persistOrder = () => {
    const map = {};
    itemsRef.current
      .filter((item) => item.id !== SUBCAT_ID)
      .forEach((item, idx) => { map[item.id] = idx + 1; });
    updateNewOrder({ items: map });
  };

  const subMeta = subItem ? typeMeta(subItem.content_type) : null;

  return (
    <>
      {loader && <RotatingLoader />}
      <Reorder.Group
        as="div"
        axis="y"
        values={realItems}
        onReorder={handleReorder}
        className={styles.clList}
      >
        {realItems.map((item) => (
          <ContentTypeRow
            key={item.id}
            item={item}
            count={counts[item.content_type]}
            commonText={commonText}
            onSettle={persistOrder}
          />
        ))}
      </Reorder.Group>

      {/* Subcategories — synthetic, non-draggable row that opens the inline creator. */}
      {subItem && (
        isOpenSubCategoryDiv ? (
          <div className={styles.clSubcatCreator}>
            <CreateSubCategory
              DragIcon={DragIndicatorRoundedIcon}
              setSubCategoryDiv={setSubCategoryDiv}
              content_type={subItem.content_type}
              isOpenSubCategoryDiv={isOpenSubCategoryDiv}
            />
          </div>
        ) : (
          <div className={cn(styles.clRow, styles.clRowStatic)}>
            <span className={styles.clDrag} style={{ visibility: 'hidden' }}>
              <DragIndicatorRoundedIcon fontSize="small" />
            </span>
            <span className={styles.clIcon} style={{ backgroundColor: subMeta.bg, color: subMeta.tint }}>
              <subMeta.Icon fontSize="small" />
            </span>
            <span className={styles.clMeta} onClick={() => setSubCategoryDiv(true)} role="button" tabIndex={0}>
              <span className={styles.clTitle}>{subItem.content_type}</span>
              <span className={styles.clSub}>Create &amp; organize subcategories</span>
            </span>
            <button type="button" className={styles.clEdit} onClick={() => setSubCategoryDiv(true)}>
              {commonText?.edit || 'Edit'}
              <ChevronRightRoundedIcon fontSize="small" />
            </button>
          </div>
        )
      )}
    </>
  );
};

// Classic (legacy production) content-type list: react-beautiful-dnd rows with
// the drag.svg handle and the original box markup. Rendered for non-allow-listed
// users so the classic /content-layout page keeps its legacy look.
const ContentGroupButtonClassic = ({ groups, isLoading, groupType }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { setAppSnackbar, langText } = useContext(AppContext);
  const commonText = langText?.common;
  const [items, setItems] = useState(groups);
  const [newOrder, updateNewOrder] = useState(undefined);
  const [currentId, setCurrentId] = useState(null);
  const [conType, setConType] = useState(undefined);
  const [loader, setloader] = useState(false);
  const [isOpenSubCategoryDiv, setSubCategoryDiv] = useState(false);
  const handleClick = (content_type, event) => {
    setAnchorEl(event.currentTarget);
    setConType(content_type === 'Photos Albums' ? 'Photoalbum' : content_type);
  };

  const { isValidating: updatingNewOrder, mutate: updateOrder } = useSWR([
    endpoints.reOrderMyContentType,
    newOrder?.items,
  ], {
    fetcher: (url, newItems) => dataFetcher(url, { items: newItems }),
    onSuccess: ({ success }) => {
      if (success) {
        setAppSnackbar({
          isVisible: true,
          message: 'Order updated',
        });
        setloader(false);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong while updating your order',
        });
        setloader(false);
      }
    },
    onError: (err) => {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Reorder Content Types',
        description: 'Content type reorder failed',
        severity: 'medium',
        errorMessage: err?.response?.data?.message || err?.message || 'Content type reorder failed',
        responseStatus: err?.response?.status || null,
        responseBody: err?.response?.data?.message || null,
        requestUrl: endpoints.reOrderMyContentType,
        requestMethod: 'POST',
        component: 'ContentGroupButton',
        userAction: 'Reorder content types called',
      });
    },
  });

  const handleClose = () => {
    setAnchorEl(null);
  };

  const newOrderList = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) { return; }

    const newItems = newOrderList(items, result.source.index, result.destination.index);
    const changedItems = {};

    items?.forEach((item, idx) => {
      if (item?.id !== newItems[idx]?.id) {
        changedItems[newItems[idx]?.id] = (idx + 1);
      }
    });

    if (result.source.index !== result.destination.index) {
      updateNewOrder({ items: changedItems });
    }

    setItems(newItems);
  };

  useEffect(() => { setItems(groups); }, [groups]);
  useEffect(() => { if (newOrder) { setloader(true); updateOrder(); } }, [newOrder]);
  return (
    <>
    {loader && <RotatingLoader />}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={groupType}>
          {(droppableProvided) => (
            <div
              className={styles.contentGroupBody}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {items
  .filter((item) => item.id !== '#$%')
  .map(({ id, content_type, content_value }, idx) => (
                <Draggable key={id} draggableId={id} index={idx}>
                  {(draggableProvided, draggableSnapshot) => (
                    <div
                      className={cn({
                        [styles.dragged]: draggableSnapshot.isDragging,
                      })}
                      key={idx}
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      style={draggableProvided.draggableProps.style}
                    >
                      <div className={styles.createButtonWrapper}>
                        {
                          id === '#$%' && isOpenSubCategoryDiv ? (
                            <CreateSubCategory DragIcon={DragIcon} setSubCategoryDiv={setSubCategoryDiv} content_type={content_type} isOpenSubCategoryDiv={isOpenSubCategoryDiv} />
                          ) : (
                            <button
                              className={cn({ [styles.contentGroupBox]: true, [styles.contentGroupButton]: true })}
                            >
                              {
                          id === '#$%' ? (
                            <>
                              <div
                                className={cn({ [styles.dragIconContainer]: true, [styles.dragIconContainerType]: true })}
                              >
                                <DragIcon className={styles.dragIcon} style={{ opacity: 0 }} />
                              </div>
                              <div
                                className={styles.contentTypeTitle}
                                onClick={() => { setSubCategoryDiv(true); }}
                              >
                                <div className={styles.titleStyle}>{content_type}</div>
                              </div>
                              <div className={styles.contentGroupBody}>
                                <div style={{ textAlign: 'center' }} className={styles.contentGroupBodyCell}>
                                  <div
                                    className={cn({ [styles.editContentTypeRef]: true, [styles.editContentTypeRefPosition]: true })}
                                    onClick={() => { setSubCategoryDiv(!isOpenSubCategoryDiv); }}
                                  >
                                    <div className={styles.editContentType}>{isOpenSubCategoryDiv ? (commonText?.back || 'Back') : (commonText?.edit || 'Edit')}</div>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                className={cn({ [styles.dragIconContainer]: true, [styles.dragIconContainerType]: true })}
                                {...draggableProvided.dragHandleProps}
                              >
                                <DragIcon className={styles.dragIcon} />
                              </div>
                              <Link
                                to={
                                  Pathname.getContentLayoutType(id)
                                }
                                // onClick={() => { localStorage.setItem('content_type', content_value); }}
                                className={styles.contentTypeTitle}
                              >
                                <div className={styles.titleStyle}>{content_type}</div>
                              </Link>
                              <div className={styles.contentGroupBody}>
                                <div style={{ textAlign: 'center' }} className={styles.contentGroupBodyCell}>
                                  <Link
                                    to={
                                      Pathname.getContentLayoutType(id)
                                    }
                                    className={cn({ [styles.editContentTypeRef]: true, [styles.editContentTypeRefPosition]: true })}
                                    // onClick={() => { localStorage.setItem('content_type', content_value); }}
                                  >
                                    <div className={styles.editContentType}>{commonText?.edit || 'Edit'}</div>
                                  </Link>
                                </div>
                              </div>
                            </>
                          )
                        }
                            </button>
                          )
                        }
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={groupType}>
          {(droppableProvided) => (
            <div
              className={styles.contentGroupBody}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
           {items
  .filter((item) => item.id === '#$%')
  .map(({ id, content_type, content_value }, idx) => (
                <Draggable key={id} draggableId={id} index={idx}>
                  {(draggableProvided, draggableSnapshot) => (
                    <div
                      className={cn({
                        [styles.dragged]: draggableSnapshot.isDragging,
                      })}
                      key={idx}
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      style={draggableProvided.draggableProps.style}
                    >
                      <div className={styles.createButtonWrapper}>
                        {
                          id === '#$%' && isOpenSubCategoryDiv ? (
                            <CreateSubCategory DragIcon={DragIcon} setSubCategoryDiv={setSubCategoryDiv} content_type={content_type} isOpenSubCategoryDiv={isOpenSubCategoryDiv} />
                          ) : (
                            <button
                              className={cn({ [styles.contentGroupBox]: true, [styles.contentGroupButton]: true })}
                            >
                              {
                          id === '#$%' ? (
                            <>
                              <div
                                className={cn({ [styles.dragIconContainer]: true, [styles.dragIconContainerType]: true })}
                              >
                                <DragIcon className={styles.dragIcon} style={{ opacity: 0 }} />
                              </div>
                              <div
                                className={styles.contentTypeTitle}
                                onClick={() => { setSubCategoryDiv(true); }}
                              >
                                <div className={styles.titleStyle}>{content_type}</div>
                              </div>
                              <div className={styles.contentGroupBody}>
                                <div style={{ textAlign: 'center' }} className={styles.contentGroupBodyCell}>
                                  <div
                                    className={cn({ [styles.editContentTypeRef]: true, [styles.editContentTypeRefPosition]: true })}
                                    onClick={() => { setSubCategoryDiv(!isOpenSubCategoryDiv); }}
                                  >
                                    <div className={styles.editContentType}>{isOpenSubCategoryDiv ? (commonText?.back || 'Back') : (commonText?.edit || 'Edit')}</div>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                className={cn({ [styles.dragIconContainer]: true, [styles.dragIconContainerType]: true })}
                                {...draggableProvided.dragHandleProps}
                              >
                                <DragIcon className={styles.dragIcon} />
                              </div>
                              <Link
                                to={
                                  Pathname.getContentLayoutType(id)
                                }
                                // onClick={() => { localStorage.setItem('content_type', content_value); }}
                                className={styles.contentTypeTitle}
                              >
                                <div className={styles.titleStyle}>{content_type}</div>
                              </Link>
                              <div className={styles.contentGroupBody}>
                                <div style={{ textAlign: 'center' }} className={styles.contentGroupBodyCell}>
                                  <Link
                                    to={
                                      Pathname.getContentLayoutType(id)
                                    }
                                    className={cn({ [styles.editContentTypeRef]: true, [styles.editContentTypeRefPosition]: true })}
                                    // onClick={() => { localStorage.setItem('content_type', content_value); }}
                                  >
                                    <div className={styles.editContentType}>{commonText?.edit || 'Edit'}</div>
                                  </Link>
                                </div>
                              </div>
                            </>
                          )
                        }
                            </button>
                          )
                        }
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

// The framer-motion command-center list (ContentGroupButtonNew) ships only to
// new-UI (allow-listed) users. Everyone else gets the legacy classic list. Same
// default export so the content-layout pages stay untouched.
const ContentGroupButton = (props) => (
  isNewUIEnabled()
    ? <ContentGroupButtonNew {...props} />
    : <ContentGroupButtonClassic {...props} />
);

export default ContentGroupButton;
